import type { Estudo } from '@sheafyield/dominio';
import { gravar, type Banco } from '@sheafyield/sync';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  banco as bancoDoApp,
  cliente,
  motor as motorDoApp,
} from '../app/contexto.js';
import {
  pessoaPorId,
  visitasDaPessoa,
  estudosDaPessoa,
} from '../dados/consultas.js';
import { donoDaSessao, type DonoDasLinhas } from '../dados/dono.js';
import { haQuantoTempo } from '../dados/tempoRelativo.js';
import { useConsultaCom, type MotorObservavel } from '../dados/useConsulta.js';
import { Folha } from '../ds/Folha.js';
import { useExclusaoDePessoa } from './FolhaExcluir.js';
import { Botao } from '../ds/Botao.js';
import { Iniciais } from '../ds/Iniciais.js';
import { Linha } from '../ds/Linha.js';
import { ProvedorDeToasts, Toast } from '../ds/Toast.js';
import { Vazio } from '../ds/Vazio.js';
import { useDiaLocal } from '../hoje/relogio.js';
import { ROTULO_DO_RESULTADO } from '../hoje/VisitasDoDia.js';
import { dataDaVisita, FolhaAgendar } from './FolhaAgendar.js';
import { FolhaEstudo, seloDoEstudo } from './FolhaEstudo.js';
import { FolhaPessoa } from './FolhaPessoa.js';
import { FolhaNota } from './FolhaNota.js';
import { ROTULO_DO_STATUS } from './rotulos.js';
import {
  lerFolhaAtivaDaFicha,
  marcarFolhaAtivaDaFicha,
  type FolhaAtivaDaFicha,
} from './rascunhoDaFicha.js';
import './pessoas.css';

const donoDoApp = donoDaSessao(cliente);
export interface DepsDeFicha {
  banco: Banco;
  motor: MotorObservavel;
  id: string;
  dono?: DonoDasLinhas;
  aoRegistrar: (pessoaId: string) => void;
  aoVoltar: () => void;
}
type Edicao =
  | { campo: 'nota'; valor: string }
  | { campo: 'proxima_visita_em'; valor: string | null };

export function PessoaTelaCom({ deps }: { deps: DepsDeFicha }) {
  return (
    <ProvedorDeToasts>
      <FichaDaPessoa deps={deps} />
    </ProvedorDeToasts>
  );
}

function FichaDaPessoa({ deps }: { deps: DepsDeFicha }) {
  const exclusao = useExclusaoDePessoa();
  const [acoesAbertas, setAcoesAbertas] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  const [folha, setFolha] = useState<FolhaAtivaDaFicha | null>(null);
  const [identidadeDaFolha, setIdentidadeDaFolha] = useState<string | null>(
    null,
  );
  const [aviso, setAviso] = useState<{
    mensagem: string;
    anterior: Edicao;
    salva: Edicao;
    id: string;
    dono: string;
  } | null>(null);
  const [erroDeDesfazer, setErroDeDesfazer] = useState<string | null>(null);
  const desfazendo = useRef(false);
  // O prazo vale só até a primeira tentativa. Limpar a mensagem de erro
  // para editar outro campo não pode tirar a segunda chance de Desfazer.
  const [avisoTentado, setAvisoTentado] = useState<typeof aviso>(null);
  const nome = useRef<HTMLHeadingElement>(null);
  const focada = useRef<string | null>(null);
  const dia = useDiaLocal();
  const ficha = useConsultaCom(
    deps.motor,
    `pessoa:${deps.id}:${String(tentativa)}`,
    async () => (await pessoaPorId(deps.banco, deps.id)) ?? null,
  );
  const historico = useConsultaCom(
    deps.motor,
    `visitas:${deps.id}:${String(tentativa)}`,
    () => visitasDaPessoa(deps.banco, deps.id),
  );
  const estudos = useConsultaCom(
    deps.motor,
    `estudos:${deps.id}:${String(tentativa)}`,
    () => estudosDaPessoa(deps.banco, deps.id),
  );
  // Conservamos a seleção após fechar para que a consulta reativa não
  // desmonte o proprietário de Desfazer, inclusive após criar um estudo.
  const [selecaoDeEstudo, setSelecaoDeEstudo] = useState<{
    identidade: string;
    estudo: Estudo | null;
  } | null>(null);
  const p = ficha.dados;
  if (
    p != null &&
    estudos.dados !== undefined &&
    selecaoDeEstudo?.identidade !== `${p.user_id}:${p.id}`
  ) {
    const ativa = lerFolhaAtivaDaFicha(p);
    const idDoEstudo = ativa?.startsWith('estudo:') ? ativa.slice(7) : 'novo';
    const escolhido = estudos.dados.find((e) => e.id === idDoEstudo) ?? null;
    setSelecaoDeEstudo({
      identidade: `${p.user_id}:${p.id}`,
      estudo: escolhido,
    });
    if (idDoEstudo !== 'novo' && escolhido === null) {
      marcarFolhaAtivaDaFicha(p, null);
      setFolha(null);
    }
  }
  if (p != null) {
    const identidade = `${p.user_id}:${p.id}`;
    // A conta vem da ficha lida. Retomamos uma vez por identidade, antes
    // de pintar, para uma atualização do banco não reabrir folha fechada.
    if (identidade !== identidadeDaFolha) {
      setIdentidadeDaFolha(identidade);
      setFolha(lerFolhaAtivaDaFicha(p));
    }
  }
  useEffect(() => {
    if (p != null && focada.current !== p.id) {
      // Na retomada, a Folha já encaminhou o foco ao formulário. O nome
      // não pode puxá-lo de volta para a ficha que está inerte atrás dela.
      if (folha === null) nome.current?.focus();
      focada.current = p.id;
    }
  }, [p, folha]);
  async function escrever(
    id: string,
    edicao: Edicao,
    donoEsperado?: string,
    salva?: Edicao,
  ) {
    const dono = await (deps.dono ?? donoDoApp)();
    if (donoEsperado !== undefined && dono !== donoEsperado)
      throw new Error('A conta mudou. Não foi possível desfazer.');
    // Reler na mesma transação evita que uma folha antiga sobrescreva um
    // contato sincronizado. Nem salvar nem desfazer ressuscitam lápides.
    return deps.banco.transaction(
      'rw',
      deps.banco.pessoa,
      deps.banco.fila,
      async () => {
        const atual = await deps.banco.pessoa.get(id);
        if (atual === undefined || atual.deleted)
          throw new Error('Esta ficha não está mais disponível.');
        if (atual.user_id !== dono)
          throw new Error('Esta ficha pertence a outra conta.');
        // Desfazer só restaura o campo se o valor efetivamente salvo ainda
        // estiver lá. Um sync posterior do mesmo campo não é nosso para apagar.
        if (salva !== undefined && atual[salva.campo] !== salva.valor)
          throw new Error(
            'A ficha foi alterada depois de salvar. Não foi possível desfazer.',
          );
        const anterior: Edicao =
          edicao.campo === 'nota'
            ? { campo: 'nota', valor: atual.nota }
            : { campo: 'proxima_visita_em', valor: atual.proxima_visita_em };
        await gravar(deps.banco, 'pessoa', {
          ...atual,
          [edicao.campo]: edicao.valor,
          user_id: dono,
        });
        return { anterior, dono };
      },
    );
  }
  async function salvar(edicao: Edicao) {
    const id = deps.id;
    const { anterior, dono } = await escrever(id, edicao);
    marcarFolhaAtivaDaFicha({ id, user_id: dono }, null);
    setAvisoTentado(null);
    setAviso({
      mensagem: edicao.campo === 'nota' ? 'Nota salva' : 'Visita agendada',
      anterior,
      salva: edicao,
      id,
      dono,
    });
    setErroDeDesfazer(null);
    setFolha(null);
  }
  async function desfazer() {
    if (aviso === null || desfazendo.current) return;
    desfazendo.current = true;
    setAvisoTentado(aviso);
    try {
      await escrever(aviso.id, aviso.anterior, aviso.dono, aviso.salva);
      setAviso(null);
      setAvisoTentado(null);
      setErroDeDesfazer(null);
    } catch (causa) {
      setErroDeDesfazer(
        causa instanceof Error
          ? causa.message
          : 'Não deu para desfazer. Tente novamente.',
      );
    } finally {
      desfazendo.current = false;
    }
  }
  const erro = ficha.erro ?? historico.erro ?? estudos.erro;
  function conteudo() {
    // undefined é “ainda lendo”; null é “não existe”. Confundir os dois
    // fazia a ficha piscar “não existe mais” em toda abertura.
    if (p === undefined)
      return erro === null ? <p role="status">Carregando ficha…</p> : null;
    if (p === null)
      return (
        <Vazio
          nivel={2}
          titulo="Esta ficha não existe mais"
          descricao="Ela pode ter sido apagada neste ou em outro aparelho."
        />
      );
    const titulo = p.nome.trim() || p.endereco.trim() || 'Pessoa sem nome';
    return (
      <>
        <header className="ficha__topo">
          {exclusao === null ? null : (
            <Botao
              variante="texto"
              aria-label="Ações"
              aria-haspopup="dialog"
              onClick={() => setAcoesAbertas(true)}
            >
              ⋯
            </Botao>
          )}
          {acoesAbertas && exclusao !== null ? (
            <Folha
              aberta
              titulo="Ações"
              aoFechar={() => setAcoesAbertas(false)}
            >
              <Botao
                variante="perigo"
                largura="cheia"
                onClick={() => {
                  setAcoesAbertas(false);
                  exclusao.solicitar(p, deps.aoVoltar);
                }}
              >
                Excluir pessoa
              </Botao>
            </Folha>
          ) : null}
          <Iniciais nome={titulo} className="ficha__iniciais" />
          {/* O h1 é o cabeçalho da casca; foco só na entrada, sem roubar das folhas. */}
          <h2 className="ficha__nome" tabIndex={-1} ref={nome}>
            {titulo}
          </h2>
          <p className="ficha__estado">{ROTULO_DO_STATUS[p.status]}</p>
          <Botao
            variante="texto"
            onClick={() => {
              setAviso(null);
              setAvisoTentado(null);
              marcarFolhaAtivaDaFicha(p, 'pessoa');
              setFolha('pessoa');
            }}
          >
            Editar
          </Botao>
        </header>
        {p.endereco.trim() === '' ? null : (
          <a
            className="ficha__endereco"
            href={`https://maps.apple.com/?q=${encodeURIComponent(p.endereco)}`}
          >
            <strong>{p.endereco}</strong>
            <span>Abrir no Mapas</span>
          </a>
        )}
        {p.telefone.trim() === '' ? null : (
          <div className="ficha__contato">
            {/* Âncoras reais: tel e sms navegam para os aplicativos do iOS. */}
            <a className="ficha__acao-contato" href={`tel:${p.telefone}`}>
              Ligar
            </a>
            <a className="ficha__acao-contato" href={`sms:${p.telefone}`}>
              Mensagem
            </a>
          </div>
        )}
        {p.proxima_visita_em === null ? null : (
          <p className="ficha__proxima">
            <span className="ficha__proxima-rotulo">Próxima visita</span>
            <time dateTime={p.proxima_visita_em}>
              {dataDaVisita(p.proxima_visita_em)}
            </time>
          </p>
        )}
        <div className="ficha__acoes">
          <Botao largura="cheia" onClick={() => deps.aoRegistrar(p.id)}>
            Registrar visita
          </Botao>
          <div className="ficha__contato">
            <Botao
              variante="secundario"
              onClick={() => {
                setAviso(null);
                setAvisoTentado(null);
                marcarFolhaAtivaDaFicha(p, 'agendar');
                setFolha('agendar');
              }}
            >
              Agendar
            </Botao>
            <Botao
              variante="secundario"
              onClick={() => {
                setAviso(null);
                setAvisoTentado(null);
                marcarFolhaAtivaDaFicha(p, 'nota');
                setFolha('nota');
              }}
            >
              Nota
            </Botao>
          </div>
        </div>
        {p.nota === '' ? null : <p className="ficha__nota">{p.nota}</p>}
        <section aria-label="Estudo">
          <h3 className="ficha__titulo-historico">Estudo</h3>
          {estudos.dados === undefined ? (
            estudos.erro === null ? (
              <p role="status">Carregando estudos…</p>
            ) : null
          ) : (
            <>
              {estudos.dados.length === 0 ? (
                <p>Sem estudo registrado.</p>
              ) : (
                <ul className="ficha__historico" aria-label="Estudos da pessoa">
                  {estudos.dados.map((e) => (
                    <Linha
                      key={e.id}
                      titulo={e.publicacao || 'Estudo bíblico'}
                      detalhe={
                        e.licao === ''
                          ? seloDoEstudo(e)
                          : `Lição ${e.licao} · ${seloDoEstudo(e)}`
                      }
                      aoTocar={() => {
                        setAviso(null);
                        setAvisoTentado(null);
                        setSelecaoDeEstudo({
                          identidade: `${p.user_id}:${p.id}`,
                          estudo: e,
                        });
                        marcarFolhaAtivaDaFicha(p, `estudo:${e.id}`);
                        setFolha(`estudo:${e.id}`);
                      }}
                    />
                  ))}
                </ul>
              )}
              <Botao
                variante="secundario"
                onClick={() => {
                  setAviso(null);
                  setAvisoTentado(null);
                  setSelecaoDeEstudo({
                    identidade: `${p.user_id}:${p.id}`,
                    estudo: null,
                  });
                  marcarFolhaAtivaDaFicha(p, 'estudo:novo');
                  setFolha('estudo:novo');
                }}
              >
                Novo estudo
              </Botao>
            </>
          )}
        </section>
        <h3 className="ficha__titulo-historico">Histórico</h3>
        {historico.dados === undefined ? (
          historico.erro === null ? (
            <p role="status">Carregando histórico…</p>
          ) : null
        ) : historico.dados.length === 0 ? (
          <p>Sem visitas registradas.</p>
        ) : (
          <ul className="ficha__historico" aria-label="Histórico de visitas">
            {historico.dados.map((v) => (
              <Linha
                key={v.id}
                titulo={ROTULO_DO_RESULTADO[v.resultado]}
                {...([v.nota, v.publicacao].filter(Boolean).length === 0
                  ? {}
                  : {
                      detalhe: [v.nota, v.publicacao]
                        .filter(Boolean)
                        .join(' · '),
                    })}
                meta={haQuantoTempo(v.em, new Date(dia))}
              />
            ))}
          </ul>
        )}
        {folha === 'nota' ? (
          <FolhaNota
            key={p.id}
            pessoa={p}
            aoFechar={() => {
              marcarFolhaAtivaDaFicha(p, null);
              setFolha(null);
            }}
            aoSalvar={(valor) => salvar({ campo: 'nota', valor })}
          />
        ) : null}
        {folha === 'agendar' ? (
          <FolhaAgendar
            key={p.id}
            pessoa={p}
            aoFechar={() => {
              marcarFolhaAtivaDaFicha(p, null);
              setFolha(null);
            }}
            aoSalvar={(valor) => salvar({ campo: 'proxima_visita_em', valor })}
          />
        ) : null}
      </>
    );
  }
  return (
    <div className="ficha">
      <Botao variante="texto" className="ficha__voltar" onClick={deps.aoVoltar}>
        Voltar para Pessoas
      </Botao>
      {erro === null ? null : (
        <div className="pessoas__erro">
          <p role="alert">
            Não deu para ler a ficha, os estudos ou o histórico. Tente
            novamente.
          </p>
          <Botao
            variante="secundario"
            onClick={() => setTentativa((n) => n + 1)}
          >
            Tentar novamente
          </Botao>
        </div>
      )}
      {conteudo()}
      {p == null ? null : (
        <FolhaPessoa
          banco={deps.banco}
          dono={deps.dono ?? donoDoApp}
          pessoa={p}
          aberta={folha === 'pessoa'}
          aoFechar={() => {
            marcarFolhaAtivaDaFicha(p, null);
            setFolha(null);
          }}
        />
      )}
      {p == null || selecaoDeEstudo === null ? null : (
        <FolhaEstudo
          banco={deps.banco}
          dono={deps.dono ?? donoDoApp}
          pessoaId={p.id}
          estudo={selecaoDeEstudo.estudo}
          aberta={folha?.startsWith('estudo:') ?? false}
          aoFechar={() => {
            marcarFolhaAtivaDaFicha(p, null);
            setFolha(null);
          }}
        />
      )}
      {erroDeDesfazer === null ? null : <p role="alert">{erroDeDesfazer}</p>}
      {/* O Toast pertence à ficha, sobrevive ao fechamento de ambas as folhas. */}
      <Toast
        chave={aviso}
        segundos={aviso !== null && avisoTentado === aviso ? 0 : 6}
        mensagem={aviso?.mensagem ?? null}
        {...(aviso === null
          ? {}
          : {
              acao: {
                rotulo: 'Desfazer',
                aoAcionar: () => {
                  void desfazer();
                },
              },
            })}
        aoDispensar={() => {
          setAviso(null);
          setAvisoTentado(null);
          setErroDeDesfazer(null);
        }}
      />
    </div>
  );
}
export function PessoaTela() {
  const { id } = useParams({ from: '/pessoas/$id' });
  const navegar = useNavigate();
  return (
    <PessoaTelaCom
      key={id}
      deps={{
        banco: bancoDoApp,
        motor: motorDoApp,
        id,
        dono: donoDoApp,
        aoVoltar: () => {
          void navegar({ to: '/pessoas' });
        },
        aoRegistrar: (pessoa) => {
          void navegar({ to: '/hoje', search: { registrar: 1, pessoa } });
        },
      }}
    />
  );
}
